import React from 'react';
import { BookOpen, BarChart3, Target, Rocket, CheckCircle2, XCircle, TrendingUp, TrendingDown } from 'lucide-react';

/**
 * Helpers de formatage locaux.
 */
const fmtPercent = (v, digits = 3) => `${((Number(v) || 0) * 100).toFixed(digits)} %`;
const fmtCurrency = (v) => new Intl.NumberFormat('fr-FR', {
  style: 'currency', currency: 'EUR', maximumFractionDigits: 0
}).format(Number(v) || 0);
const fmtNumber = (v, digits = 2) => new Intl.NumberFormat('fr-FR', {
  minimumFractionDigits: digits, maximumFractionDigits: digits
}).format(Number(v) || 0);

/**
 * Encart pédagogique unifié : règle légale, chiffres clés, diagnostic, pistes d'action.
 *
 * Props :
 *  - type: 'ticfe' | 'ticgn' | 'turpe'
 *  - eligible: bool  (TICFE/TICGN) — pour TURPE, mettre `optimisable`
 *  - data: objet contenant les champs métier (ratio, seuil, vac, conso, taxe, eco, etc.)
 *  - accentColor: couleur d'accent hex (ex: '#397DFF' pour TICFE, '#F97316' pour TICGN, '#0891B2' pour TURPE)
 */
export default function EligibilityExplainer({ type, eligible, data = {}, accentColor = '#397DFF' }) {
  const content = buildContent(type, eligible, data);
  const accent = accentColor;

  return (
    <div className="bg-white rounded-xl border-2 border-gray-200 overflow-hidden" data-testid={`eligibility-explainer-${type}`}>
      <div className="px-6 py-4 border-b border-gray-100 bg-gray-50">
        <h3 className="text-lg font-semibold text-gray-900 flex items-center gap-2">
          <BookOpen className="w-5 h-5" style={{ color: accent }} />
          Comprendre votre {content.subjectShort}
        </h3>
        <p className="text-sm text-gray-500 mt-1">{content.intro}</p>
      </div>

      <div className="p-6 space-y-6">
        {/* 1) Règle légale */}
        <section>
          <div className="flex items-start gap-3 mb-2">
            <div className="p-2 rounded-lg" style={{ backgroundColor: `${accent}15` }}>
              <BookOpen className="w-4 h-4" style={{ color: accent }} />
            </div>
            <div className="flex-1">
              <h4 className="font-semibold text-gray-900 text-sm">📜 La règle légale</h4>
              <div className="mt-1 text-sm text-gray-700 leading-relaxed">
                {content.rule}
              </div>
            </div>
          </div>
        </section>

        {/* 2) Chiffres clés */}
        <section>
          <div className="flex items-start gap-3 mb-3">
            <div className="p-2 rounded-lg" style={{ backgroundColor: `${accent}15` }}>
              <BarChart3 className="w-4 h-4" style={{ color: accent }} />
            </div>
            <div className="flex-1">
              <h4 className="font-semibold text-gray-900 text-sm">📊 Les chiffres clés de votre dossier</h4>
            </div>
          </div>
          <div className="ml-11 grid grid-cols-2 lg:grid-cols-4 gap-3">
            {content.keyFigures.map((f, idx) => (
              <div key={idx} className="bg-gray-50 rounded-lg p-3 border border-gray-100">
                <div className="text-xs text-gray-500 mb-1">{f.label}</div>
                <div className="font-mono font-bold text-gray-900" style={{ color: f.highlight ? accent : undefined }}>
                  {f.value}
                </div>
                {f.hint && <div className="text-[11px] text-gray-400 mt-0.5">{f.hint}</div>}
              </div>
            ))}
          </div>
        </section>

        {/* 3) Diagnostic */}
        <section>
          <div className="flex items-start gap-3 mb-2">
            <div className={`p-2 rounded-lg ${eligible ? 'bg-emerald-50' : 'bg-red-50'}`}>
              {eligible
                ? <CheckCircle2 className="w-4 h-4 text-emerald-600" />
                : <XCircle className="w-4 h-4 text-red-500" />}
            </div>
            <div className="flex-1">
              <h4 className="font-semibold text-gray-900 text-sm">🎯 Notre diagnostic</h4>
              <div className={`mt-2 rounded-lg p-4 border-l-4 ${eligible ? 'bg-emerald-50 border-emerald-400 text-emerald-900' : 'bg-red-50 border-red-400 text-red-900'}`}>
                <div className="text-sm font-semibold mb-1">{content.diagnosis.headline}</div>
                <div className="text-sm leading-relaxed">{content.diagnosis.body}</div>
              </div>
            </div>
          </div>
        </section>

        {/* 4) Pistes d'action */}
        <section>
          <div className="flex items-start gap-3 mb-2">
            <div className="p-2 rounded-lg" style={{ backgroundColor: `${accent}15` }}>
              <Rocket className="w-4 h-4" style={{ color: accent }} />
            </div>
            <div className="flex-1">
              <h4 className="font-semibold text-gray-900 text-sm">🚀 Vos prochaines étapes</h4>
              <ul className="mt-2 space-y-2">
                {content.actions.map((a, idx) => (
                  <li key={idx} className="flex items-start gap-2 text-sm text-gray-700">
                    <span className="mt-1.5 w-1.5 h-1.5 rounded-full flex-shrink-0" style={{ backgroundColor: accent }} />
                    <span>{a}</span>
                  </li>
                ))}
              </ul>
            </div>
          </div>
        </section>
      </div>
    </div>
  );
}

/**
 * Construit dynamiquement le contenu pédagogique selon le type de rapport.
 */
function buildContent(type, eligible, d) {
  const SEUIL = 0.005; // 0,5%

  if (type === 'ticfe') {
    const ratio = Number(d.ratio_global) || 0;
    const vac = Number(d.valeur_ajoutee) || 0;
    const conso = Number(d.total_conso_mwh) || 0;
    const ticfeCerfa = Number(d.ticfe_cerfa) || 0;
    const tarifCerfa = 22.5; // €/MWh
    // Pour devenir éligible si non éligible : calculer la conso supplémentaire OU la baisse VAC
    const ticfeRequis = SEUIL * vac; // € requis
    const consoSupplementaireMwh = Math.max(0, (ticfeRequis - ticfeCerfa) / tarifCerfa);
    const vacMaxEuros = ticfeCerfa / SEUIL; // VAC max pour atteindre 0.5%
    const baisseVacEuros = Math.max(0, vac - vacMaxEuros);

    return {
      subjectShort: 'éligibilité TICFE',
      intro: 'Pourquoi votre entreprise est-elle (ou non) éligible au taux réduit / remboursement TICFE ?',
      rule: (
        <>
          La <strong>TICFE</strong> (Taxe Intérieure sur la Consommation Finale d'Électricité) prévoit des
          <strong> taux réduits</strong> pour les entreprises dites <em>électro-intensives</em> ou en
          <em> risque de fuite carbone</em> (art. 266 quinquies C du Code des douanes).
          Une entreprise est éligible quand son <strong>ratio TICFE théorique / Valeur Ajoutée Comptable (VAC) ≥ 0,5 %</strong>.
          La TICFE théorique est calculée au tarif administratif unique de <strong>22,5 €/MWh</strong> (référence CERFA).
        </>
      ),
      keyFigures: [
        { label: 'Votre ratio TICFE/VA', value: fmtPercent(ratio, 3), highlight: true, hint: ratio >= SEUIL ? '≥ seuil légal' : '< seuil légal' },
        { label: 'Seuil légal', value: '0,500 %', hint: 'Article 266 quinquies C' },
        { label: 'Valeur Ajoutée (VAC)', value: fmtCurrency(vac), hint: 'Liasse fiscale 2058-A' },
        { label: 'Consommation annuelle', value: `${fmtNumber(conso, 1)} MWh`, hint: `≈ ${fmtCurrency(ticfeCerfa)} TICFE CERFA` },
      ],
      diagnosis: eligible ? {
        headline: '✓ Votre entreprise est éligible au dispositif TICFE réduit',
        body: `Votre ratio de ${fmtPercent(ratio, 3)} dépasse le seuil légal de 0,500 %. Vous pouvez à la fois (a) faire appliquer un taux réduit en attestation auprès de votre fournisseur pour les 12 prochains mois, et (b) déposer un dossier CERFA de remboursement sur les 2 années passées.`
      } : {
        headline: '✗ Votre entreprise n\'est pas éligible aujourd\'hui',
        body: `Votre ratio de ${fmtPercent(ratio, 3)} est inférieur au seuil légal de 0,500 %. Cependant, votre éligibilité peut évoluer chaque année selon votre consommation et votre VAC. Pour atteindre le seuil aujourd'hui, il faudrait soit augmenter votre consommation de ${fmtNumber(consoSupplementaireMwh, 1)} MWh/an, soit voir votre VAC baisser de ${fmtCurrency(baisseVacEuros)} (pour atteindre ${fmtCurrency(vacMaxEuros)}).`
      },
      actions: eligible ? [
        'Étape 1 — Vérifier les conditions de validité (NAF électro-intensive ou fuite carbone, code procédé).',
        'Étape 2 — Préparer et signer l\'attestation d\'éligibilité TICFE (modèle CERFA n°14318*04) à transmettre à votre fournisseur d\'énergie.',
        'Étape 3 — Constituer le dossier CERFA n°14571*01 (remboursement TICFE) auprès de l\'administration fiscale pour les 2 années passées.',
        'Étape 4 — Place des Énergies coordonne avec votre expert-comptable la déclaration finale et le suivi du remboursement (délai habituel : 3 à 18 mois).',
      ] : [
        'Refaire le calcul chaque année à la clôture comptable (le ratio évolue avec la VAC et la consommation).',
        'Si vos investissements futurs augmentent significativement la consommation (nouveau process industriel, électrification), demandez-nous une nouvelle simulation.',
        'Pour les entreprises proches du seuil, plusieurs leviers existent (refacturation interne, périmètre d\'analyse) : nous pouvons les explorer ensemble.',
        'Conservez ce rapport : il pourra servir de référence pour démontrer la non-éligibilité auprès de votre fournisseur ou de l\'administration.',
      ],
    };
  }

  if (type === 'ticgn') {
    const ratio = Number(d.ratio_global) || 0;
    const vac = Number(d.valeur_ajoutee) || 0;
    const conso = Number(d.total_conso_mwh) || 0;
    const ticgnCerfa = Number(d.ticgn_cerfa) || 0;
    const tarifCerfa = 8.37; // €/MWh
    const taxeRequise = SEUIL * vac;
    const consoSupplementaire = Math.max(0, (taxeRequise - ticgnCerfa) / tarifCerfa);
    const vacMax = ticgnCerfa / SEUIL;
    const baisseVac = Math.max(0, vac - vacMax);

    return {
      subjectShort: 'éligibilité TICGN',
      intro: 'Pourquoi votre entreprise est-elle (ou non) éligible au taux réduit / remboursement TICGN ?',
      rule: (
        <>
          La <strong>TICGN</strong> (Taxe Intérieure sur la Consommation de Gaz Naturel) prévoit des
          <strong> taux réduits</strong> pour les entreprises <em>grandes consommatrices d'énergie</em> dont la facture
          énergétique pèse de manière significative sur leur Valeur Ajoutée (art. 266 quinquies du Code des douanes).
          Le test légal est <strong>somme des taxes énergétiques (gaz, électricité, etc.) / VAC ≥ 0,5 %</strong>,
          calculée au tarif administratif unique de <strong>8,37 €/MWh</strong> (référence CERFA gaz).
        </>
      ),
      keyFigures: [
        { label: 'Votre ratio multi-énergies', value: fmtPercent(ratio, 3), highlight: true, hint: ratio >= SEUIL ? '≥ seuil légal' : '< seuil légal' },
        { label: 'Seuil légal', value: '0,500 %', hint: 'Article 266 quinquies' },
        { label: 'Valeur Ajoutée (VAC)', value: fmtCurrency(vac), hint: 'Liasse fiscale 2058-A' },
        { label: 'Consommation gaz annuelle', value: `${fmtNumber(conso, 1)} MWh`, hint: `≈ ${fmtCurrency(ticgnCerfa)} TICGN CERFA` },
      ],
      diagnosis: eligible ? {
        headline: '✓ Votre entreprise est éligible au dispositif TICGN réduit',
        body: `Votre ratio de ${fmtPercent(ratio, 3)} dépasse le seuil légal de 0,500 %. Vous pouvez appliquer le taux réduit via une attestation auprès de votre fournisseur de gaz et déposer un dossier CERFA pour récupérer la TICGN payée sur les 2 années passées.`
      } : {
        headline: '✗ Votre entreprise n\'est pas éligible aujourd\'hui',
        body: `Votre ratio multi-énergies de ${fmtPercent(ratio, 3)} est inférieur au seuil légal de 0,500 %. Pour atteindre le seuil, il faudrait soit augmenter votre consommation de gaz de ${fmtNumber(consoSupplementaire, 1)} MWh/an (au tarif CERFA), soit voir votre VAC baisser de ${fmtCurrency(baisseVac)} (pour atteindre ${fmtCurrency(vacMax)}).`
      },
      actions: eligible ? [
        'Étape 1 — Identifier les PCE/sites éligibles dans votre périmètre.',
        'Étape 2 — Préparer l\'attestation TICGN à transmettre à votre fournisseur de gaz pour les 12 prochains mois.',
        'Étape 3 — Constituer le dossier CERFA de remboursement TICGN sur les 2 années passées (à confirmer après réception des factures).',
        'Étape 4 — Place des Énergies coordonne avec votre expert-comptable et suit le dossier jusqu\'au remboursement effectif.',
      ] : [
        'Le test étant multi-énergies (gaz + électricité + autres), une consommation accrue d\'électricité éligible peut faire basculer le ratio global.',
        'Refaire l\'analyse chaque année à la clôture (le ratio évolue avec la VAC et les consommations).',
        'Si vous prévoyez de basculer un process vers le gaz ou d\'augmenter votre activité, contactez-nous pour une nouvelle simulation.',
        'Conservez ce rapport comme preuve d\'analyse pour justifier votre situation auprès des tiers (fournisseur, administration).',
      ],
    };
  }

  // === TURPE === (optimisation, pas éligibilité au sens strict)
  const economie = Number(d.economie_totale) || 0;
  const coutActuel = Number(d.cout_actuel) || 0;
  const coutOptimise = Number(d.cout_optimise) || 0;
  const reactifEco = Number(d.economies_reactif) || 0;
  const optimisable = eligible; // eligible = optimisation possible
  const pctEcono = coutActuel > 0 ? (economie / coutActuel) : 0;

  return {
    subjectShort: 'optimisation TURPE',
    intro: 'Pourquoi pouvons-nous (ou non) optimiser votre contrat d\'acheminement Enedis ?',
    rule: (
      <>
        Le <strong>TURPE</strong> (Tarif d'Utilisation des Réseaux Publics d'Électricité) facture
        <strong> la puissance souscrite</strong> indépendamment de l'énergie consommée. Souscrire une puissance
        supérieure à votre besoin réel = paiement d'une part fixe inutile. À l'inverse, sous-souscrire entraîne
        des <em>dépassements</em> facturés cher. L'optimisation consiste à <strong>aligner précisément la puissance
        souscrite sur votre courbe de charge réelle</strong> (analyse pas 10 min sur 12 mois). Une marge de sécurité
        est conservée pour éviter les dépassements. Le calcul intègre aussi les <strong>pénalités de réactif</strong>
        (cos φ {'<'} 0,93) qui peuvent peser 5 à 15 % de la facture.
      </>
    ),
    keyFigures: [
      { label: 'Coût TURPE actuel', value: fmtCurrency(coutActuel), hint: 'Sur 12 mois' },
      { label: 'Coût TURPE optimisé', value: fmtCurrency(coutOptimise), highlight: true, hint: 'Cible recommandée' },
      { label: 'Économie annuelle', value: fmtCurrency(economie), highlight: true, hint: pctEcono > 0 ? `Soit ${fmtNumber(pctEcono * 100, 1)} % d'économie` : null },
      { label: 'dont pénalités réactif', value: fmtCurrency(reactifEco), hint: 'Si réinjection capacitif ou cos φ bas' },
    ],
    diagnosis: optimisable ? {
      headline: '✓ Optimisation TURPE recommandée',
      body: `L'analyse de votre courbe de charge montre un potentiel d'économie annuel de ${fmtCurrency(economie)}, soit ${fmtNumber(pctEcono * 100, 1)} % de votre coût TURPE actuel. Cette économie provient principalement de l'ajustement de votre puissance souscrite par plage tarifaire${reactifEco > 0 ? ` et de la correction des pénalités réactif (${fmtCurrency(reactifEco)}/an récupérables via batterie de condensateurs)` : ''}.`
    } : {
      headline: '✓ Votre contrat est déjà bien dimensionné',
      body: 'L\'analyse ne révèle pas d\'écart significatif entre votre puissance souscrite et votre besoin réel. Votre contrat TURPE est correctement calibré et nous ne recommandons pas de modification dans l\'immédiat.'
    },
    actions: optimisable ? [
      'Étape 1 — Valider la cohérence de la courbe de charge avec votre activité (pas de pic isolé sur travaux exceptionnels par exemple).',
      'Étape 2 — Vérifier l\'absence de projets impactants sur 12 mois (nouvelle ligne de production, électrification).',
      reactifEco > 0
        ? 'Étape 3 — Installer une batterie de condensateurs pour corriger le facteur de puissance (ROI typique : 12-24 mois).'
        : 'Étape 3 — Demander à Enedis la modification du contrat aux nouvelles puissances proposées.',
      'Étape 4 — Place des Énergies prépare le bon de commande Enedis et suit l\'application effective de la nouvelle puissance souscrite (sous 30 jours).',
    ] : [
      'Refaire l\'analyse à la prochaine échéance contractuelle ou en cas de changement de profil (nouveau process, fermeture, extension).',
      'Surveiller les pénalités réactif sur les prochaines factures : un cos φ qui dérive sous 0,93 ouvre une nouvelle opportunité d\'optimisation.',
      'Conserver ce rapport comme attestation de bon dimensionnement (utile en cas d\'audit interne ou d\'appel d\'offres énergie).',
    ],
  };
}
